// =============================================================================
// MeuDriver — M1: Histórico Financeiro e Projeções do Instrutor
// =============================================================================

import { useState } from 'react'
import { startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns'
import {
  TrendingUp,
  DollarSign,
  BarChart2,
  Calendar,
  Award,
  BookOpen,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  useEarningsSummary,
  useEarningsHistory,
  useEarningsProjection,
  useTopStudents,
} from '@/hooks/useInstructorEarnings'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency } from '@/lib/utils'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Tipos de período
// ---------------------------------------------------------------------------

type PeriodKey = 'mes' | '3m' | '6m' | '12m'

interface Period {
  key: PeriodKey
  label: string
  start: Date
  end: Date
}

function buildPeriods(): Period[] {
  const now = new Date()
  return [
    { key: 'mes', label: 'Mês atual', start: startOfMonth(now), end: endOfMonth(now) },
    { key: '3m',  label: 'Últimos 3 meses', start: startOfMonth(subMonths(now, 2)), end: endOfMonth(now) },
    { key: '6m',  label: 'Últimos 6 meses', start: startOfMonth(subMonths(now, 5)), end: endOfMonth(now) },
    { key: '12m', label: 'Últimos 12 meses', start: startOfYear(now), end: endOfYear(now) },
  ]
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

function KpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconClass,
  loading,
}: {
  title: string
  value: string
  subtitle?: string
  icon: React.ElementType
  iconClass: string
  loading: boolean
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={cn('h-4 w-4', iconClass)} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <>
            <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Tooltip customizado do Recharts
// ---------------------------------------------------------------------------

function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border bg-white dark:bg-gray-900 p-3 shadow-lg text-sm space-y-1">
      <p className="font-semibold text-gray-700 dark:text-gray-200">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: <span className="font-medium">{formatCurrency(p.value)}</span>
        </p>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function InstructorEarnings() {
  const periods = buildPeriods()
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('mes')
  const period = periods.find((p) => p.key === activePeriod)!

  const { data: summary, isLoading: summaryLoading } = useEarningsSummary(
    period.start,
    period.end,
  )
  const { data: history, isLoading: historyLoading } = useEarningsHistory(6)
  const { projection, isLoading: projLoading } = useEarningsProjection()
  const { data: topStudents, isLoading: topLoading } = useTopStudents(5)

  const now = new Date()
  const { data: yearSummary, isLoading: yearLoading } = useEarningsSummary(
    startOfYear(now),
    endOfYear(now),
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-teal-600" />
          Ganhos
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Acompanhe seu histórico financeiro e projeções.
        </p>
      </div>

      {/* Seletor de período */}
      <div className="flex flex-wrap gap-2">
        {periods.map((p) => (
          <button
            key={p.key}
            data-testid={`period-btn-${p.key}`}
            onClick={() => setActivePeriod(p.key)}
            className={cn(
              'rounded-lg px-4 py-1.5 text-sm font-medium transition-all',
              activePeriod === p.key
                ? 'bg-teal-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Líquido no período"
          value={formatCurrency(summary?.total_net ?? 0)}
          subtitle={period.label}
          icon={DollarSign}
          iconClass="text-teal-600"
          loading={summaryLoading}
        />
        <KpiCard
          title="Total no ano"
          value={formatCurrency(yearSummary?.total_net ?? 0)}
          subtitle={`${now.getFullYear()}`}
          icon={Calendar}
          iconClass="text-blue-600"
          loading={yearLoading}
        />
        <KpiCard
          title="Média por aula"
          value={formatCurrency(summary?.avg_per_lesson ?? 0)}
          subtitle={`${summary?.completed_lessons ?? 0} aulas no período`}
          icon={BookOpen}
          iconClass="text-brand-600"
          loading={summaryLoading}
        />
        <KpiCard
          title="Projeção próx. mês"
          value={formatCurrency(projection)}
          subtitle="média últimos 3 meses"
          icon={BarChart2}
          iconClass="text-purple-600"
          loading={projLoading}
        />
      </div>

      {/* Gráfico de barras — últimos 6 meses */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ganhos mensais — últimos 6 meses</CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : !history || history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Nenhum dado disponível.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={history} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`
                  }
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="gross" name="Bruto" fill="#94d5cc" radius={[3, 3, 0, 0]} />
                <Bar dataKey="net" name="Líquido" fill="#0d9488" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tabela histórico paginada */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Resumo do período</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
              </div>
            ) : !summary ? (
              <p className="text-sm text-muted-foreground">Sem dados no período.</p>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Receita bruta</span>
                  <span className="font-medium">{formatCurrency(summary.total_gross)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxas plataforma</span>
                  <span className="font-medium text-orange-600">- {formatCurrency(summary.platform_fees)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="font-semibold">Receita líquida</span>
                  <span className="font-bold text-teal-700">{formatCurrency(summary.total_net)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aulas concluídas</span>
                  <span className="font-medium">{summary.completed_lessons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aulas canceladas</span>
                  <span className="font-medium text-red-500">{summary.cancelled_lessons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dias com aulas</span>
                  <span className="font-medium">{summary.days_with_lessons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Média por aula</span>
                  <span className="font-medium">{formatCurrency(summary.avg_per_lesson)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 5 alunos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4 text-yellow-500" />
              Top 5 alunos por receita
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : !topStudents || topStudents.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum dado disponível.</p>
            ) : (
              <div className="space-y-3">
                {topStudents.map((s, idx) => (
                  <div key={s.student_id} className="flex items-center gap-3">
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                        idx === 0
                          ? 'bg-yellow-100 text-yellow-700'
                          : idx === 1
                          ? 'bg-gray-100 text-gray-600'
                          : idx === 2
                          ? 'bg-orange-100 text-orange-600'
                          : 'bg-slate-100 text-slate-500',
                      )}
                    >
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {s.first_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{s.lesson_count} aulas</p>
                    </div>
                    <span className="text-sm font-semibold text-teal-700 shrink-0">
                      {formatCurrency(s.total_net)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
